import React from 'react';
import { Link } from 'react-router-dom';
import {
  COMPANY, SUPPORT_EMAIL, PRIVACY_EMAIL, COPYRIGHT_EMAIL, BRAND, SITE_URL, LANGUAGE, localeUrl,
} from '../config/region';
import { t } from '../i18n';
import LegalPage from './LegalPage';
import './LegalInfoPage.css';

/*
 * Operator identification ("legal notice" / Impressum).
 *
 * An EU-facing service has to be identifiable without the reader hunting through a contract: who
 * operates it, under what registration, at what address, and which mailbox answers which kind of
 * request. That is what this page is for — it repeats details from the documents rather than
 * adding terms of its own.
 */
const Row = ({ label, children }) => (
  <div className="li-row">
    <span className="li-label">{label}</span>
    <span className="li-value">{children}</span>
  </div>
);

const LegalInfoPage = () => {
  const description = t(
    'Сведения об операторе платформы {0}: юридическое наименование, регистрационный номер, адрес и контакты для обращений.',
    BRAND
  );

  /* Organization markup: the identity block is exactly what a knowledge panel wants, and it is
     the one page on the site whose facts are stable enough to assert. */
  const jsonLd = COMPANY && {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: BRAND,
    legalName: COMPANY.legalName,
    url: SITE_URL,
    logo: `${SITE_URL}/logo512.png`,
    email: SUPPORT_EMAIL,
    telephone: COMPANY.phone,
    identifier: { '@type': 'PropertyValue', name: COMPANY.registrationLabel, value: COMPANY.registrationNumber },
    address: { '@type': 'PostalAddress', streetAddress: COMPANY.address },
    contactPoint: [
      { '@type': 'ContactPoint', contactType: 'customer support', email: SUPPORT_EMAIL },
      { '@type': 'ContactPoint', contactType: 'copyright', email: COPYRIGHT_EMAIL },
      { '@type': 'ContactPoint', contactType: 'privacy', email: PRIVACY_EMAIL },
    ],
  };

  if (!COMPANY) return null;

  return (
    <LegalPage title={t('Юридическая информация')} description={description} jsonLd={jsonLd}>
      <p>
        {t('{0} — платформа интерактивных новелл. Ниже приведены сведения об операторе платформы и контакты для обращений.', BRAND)}
      </p>

      <h2>{t('Оператор платформы')}</h2>
      <div className="li-table">
        <Row label={t('Наименование')}>{COMPANY.legalName}</Row>
        {LANGUAGE === 'russian' && <Row label={t('Наименование (рус.)')}>{COMPANY.localName}</Row>}
        <Row label={COMPANY.registrationLabel}>{COMPANY.registrationNumber}</Row>
        <Row label={t('Юридический адрес')}>
          {LANGUAGE === 'russian' ? COMPANY.localAddress : COMPANY.address}
        </Row>
        <Row label={t('Телефон')}>
          <a href={`tel:${COMPANY.phone.replace(/[^+\d]/g, '')}`}>{COMPANY.phone}</a>
        </Row>
        <Row label={t('Сайт')}>
          <a href={SITE_URL}>{SITE_URL.replace(/^https?:\/\//, '')}</a>
        </Row>
      </div>

      <h2>{t('Контакты для обращений')}</h2>
      <p>{t('Чтобы обращение попало к нужным людям, используйте адрес, соответствующий его теме.')}</p>
      <div className="li-table">
        <Row label={t('Общие вопросы и поддержка')}>
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </Row>
        <Row label={t('Жалобы правообладателей')}>
          <a href={`mailto:${COPYRIGHT_EMAIL}`}>{COPYRIGHT_EMAIL}</a>
          <span className="li-hint">
            {' — '}
            <Link to="/copyright">{t('форма и требования к жалобе')}</Link>
          </span>
        </Row>
        <Row label={t('Запросы по персональным данным')}>
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>
          <span className="li-hint">
            {' — '}
            <Link to="/privacy-request">{t('доступ, исправление, удаление данных')}</Link>
          </span>
        </Row>
      </div>

      <h2>{t('Документы')}</h2>
      <ul>
        <li><Link to="/terms">{t('Пользовательское соглашение')}</Link></li>
        <li><Link to="/privacy">{t('Политика конфиденциальности')}</Link></li>
        <li><Link to="/cookies">{t('Политика использования файлов cookie')}</Link></li>
        <li><Link to="/copyright">{t('Правообладателям')}</Link></li>
      </ul>

      <h2>{t('Статус платформы')}</h2>
      <p>
        {t('Платформа является хостинг-провайдером в значении статьи 6 Регламента о цифровых услугах (DSA) и interactive computer service в значении раздела 230 Communications Decency Act (США). Мы не осуществляем предварительную модерацию пользовательского контента и не являемся его издателем. Ответственность за размещённый контент несёт создавший его пользователь.')}
      </p>
      <p>
        {t('Актуальные редакции документов всегда доступны по ссылкам выше. Канонический адрес этой страницы:')}{' '}
        <a href={localeUrl('/legal')}>{localeUrl('/legal')}</a>
      </p>
    </LegalPage>
  );
};

export default LegalInfoPage;
